<template>
  <!-- Tab Navigation -->
  <div class="flex justify-center bg-gray-800 border-b border-gray-700 sticky top-0 z-10">
    <button
      v-for="tab in tabs"
      :key="tab.id"
      @click="activeTab = tab.id"
      :class="[
        'px-4 py-2 text-sm font-medium transition-colors duration-200 relative',
        activeTab === tab.id
          ? 'text-indigo-300 border-b-2 border-indigo-600'
          : 'text-gray-300 hover:text-indigo-300 hover:bg-gray-700'
      ]"
    >
      {{ tab.name }}
      <span
        v-if="activeTab === tab.id"
        class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"
      ></span>
    </button>
  </div>
  <div
    class="p-3 pb-20 bg mb-auto h-full overflow-auto bg-gray-900 text-white scrollbar-thin"
    id="style-1"
  >
    <div class="p-4 max-w-7xl mx-auto">
      <!-- Notification -->
      <transition name="fade">
        <div
          v-if="notification"
          class="fixed top-4 right-4 p-3 rounded-lg shadow-xl z-50 max-w-sm bg-gray-800 border animate-fadeIn"
          :class="notification.type === 'success'
              ? 'text-green-300 border-green-600'
              : 'text-red-300 border-red-600'"
        >
          <div class="flex items-center space-x-3">
            <i
              :class="notification.type === 'success'
                  ? 'fas fa-check-circle'
                  : 'fas fa-exclamation-triangle'"
              class="text-sm"
            ></i>
            <span class="text-sm">{{ notification.message }}</span>
            <button
              @click="notification = null"
              class="ml-auto text-sm text-gray-300 hover:text-indigo-300 transition-all duration-200 group"
            >
              <i class="fas fa-times text-sm group-hover:scale-110"></i>
            </button>
          </div>
        </div>
      </transition>
      <!-- Tab Content -->
      <div class="animate-fadeIn">
        <!-- Topup Tab -->
        <div v-if="activeTab === 'topup'" class="space-y-4">
          <!-- Admin Status Notification -->
          <div
            class="p-3 rounded-lg shadow-xl max-w-full bg-gray-800 border animate-fadeIn"
            :class="online ? 'text-green-300 border-green-600' : 'text-yellow-300 border-yellow-600'"
          >
            <div class="flex items-center justify-center space-x-3">
              <i :class="online ? 'fas fa-user-check' : 'fas fa-user-clock'" class="text-sm"></i>
              <span class="text-sm">
                {{
                  online
                    ? 'ขณะนี้ทีมงานออนไลน์อยู่ คำขอเติมเงินจะได้รับการดำเนินการภายใน 1-3 นาที'
                    : 'การดำเนินการล่าช้าอาจจะได้รับอนุมัติภายใน 3-4 ชั่วโมง (ช่วงเวลาทำการ 12.00 น. - 04.00 น.)'
                }}
              </span>
            </div>
          </div>
          <!-- QR Code Payment -->
          <div class="bg-gray-900 bg-opacity-80 border border-gray-800 rounded-md p-4 shadow">
            <div class="flex items-center mb-4 space-x-3">
              <i class="fas fa-qrcode text-indigo-300 text-sm"></i>
              <h2 class="text-lg font-semibold text-indigo-300">สแกนเพื่อโอนเงิน</h2>
            </div>
            <div class="flex justify-center">
              <img
                src="https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/scan.PNG?alt=media&token=8d77f71b-c5d2-48a8-bb1d-847b6bd91f66"
                alt="QR Code"
                class="w-48 h-48 object-contain border border-indigo-600 rounded-md"
              />
            </div>
            <p class="text-center text-sm text-indigo-200 mt-2">กรุณาตรวจสอบชื่อบัญชีก่อนโอน</p>
          </div>
          <!-- Bank Info -->
          <div class="bg-gray-900 bg-opacity-80 border border-gray-800 rounded-md p-4 shadow">
            <div class="flex items-center mb-4 space-x-3">
              <i class="fas fa-university text-indigo-300 text-sm"></i>
              <h2 class="text-lg font-semibold text-indigo-300">บัญชีโอนเงิน</h2>
            </div>
            <ul class="space-y-2 text-sm text-indigo-200">
              <li><strong class="text-indigo-300">ธนาคาร:</strong> กสิกร</li>
              <li><strong class="text-indigo-300">เลขที่บัญชี:</strong> 7952146240</li>
              <li><strong class="text-indigo-300">ชื่อบัญชี:</strong> Athikom Wong</li>
            </ul>
          </div>
          <!-- Bonus Notification (ถ้ามี) -->
          <div
            v-if="bonusActive"
            class="p-3 rounded-lg shadow-xl max-w-full bg-gray-800 border border-purple-600 text-purple-300 animate-fadeIn"
          >
            <div class="flex items-center justify-center space-x-3">
              <i class="fas fa-gift text-sm"></i>
              <span class="text-sm">
                โบนัสเติมเงิน {{ bonusPercentage }}%! {{ bonusMessage }} (เหลือเวลา: {{ bonusCountdown }})
              </span>
            </div>
          </div>
          <!-- Amount Options -->
          <div>
            <h3 class="text-lg font-semibold text-indigo-300 mb-3">เลือกจำนวนเงิน</h3>
            <div class="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <button
                v-for="opt in topupOptions"
                :key="opt.amount"
                @click="selectTopupOption(opt)"
                :class="[
                  'flex items-center justify-center p-3 rounded-md font-semibold transition-all duration-200 text-sm border',
                  selectedOption?.amount === opt.amount
                    ? 'bg-gray-700 border-indigo-600 text-indigo-300'
                    : 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 hover:text-indigo-300'
                ]"
              >
                {{ opt.amount }} ฿ → {{ calculateCashWithBonus(opt.cash) }}
                <span
                  v-if="bonusActive && calculateCashWithBonus(opt.cash) > opt.cash"
                  class="ml-1 text-green-300"
                >
                  (+{{ calculateCashWithBonus(opt.cash) - opt.cash }})
                </span>
                <i class="fas fa-money-bill ml-1 text-green-500 text-sm"></i>
              </button>
            </div>
          </div>
          <!-- Transfer Details -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-indigo-200 mb-1">วันที่และเวลาโอน</label>
              <div class="relative">
                <i class="fas fa-calendar-alt absolute top-3 left-3 text-indigo-300 text-sm"></i>
                <input
                  type="datetime-local"
                  v-model="transferTime"
                  class="w-full pl-10 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-indigo-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
                />
              </div>
              <p v-if="transferTimeError" class="text-red-300 text-sm mt-1">{{ transferTimeError }}</p>
            </div>
            <div>
              <label class="block text-sm font-medium text-indigo-200 mb-1">อัปโหลดใบเสร็จ</label>
              <label
                class="flex items-center p-2 bg-gray-700 border border-gray-600 rounded-md cursor-pointer hover:bg-gray-600 transition-all duration-200"
              >
                <i class="fas fa-upload mr-2 text-indigo-300 text-sm"></i>
                <span v-if="!selectedFile" class="text-sm text-gray-300">เลือกไฟล์ (.jpg, .png)</span>
                <span v-else class="truncate text-sm text-indigo-200">{{ selectedFile.name }}</span>
                <input type="file" accept="image/jpeg,image/png" @change="handleFileUpload" class="hidden" />
              </label>
              <p v-if="fileError" class="text-red-300 text-sm mt-1">{{ fileError }}</p>
            </div>
          </div>
          <!-- Terms -->
          <div class="flex items-center">
            <input
              type="checkbox"
              v-model="acceptTerms"
              class="h-5 w-5 bg-gray-700 rounded text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600 border border-gray-600"
            />
            <label class="ml-2 text-sm text-indigo-200">ยอมรับว่าเงินที่โอนแล้วไม่สามารถขอคืนได้</label>
          </div>
          <!-- Submit Button -->
          <button
            @click="submitTopupRequest"
            :disabled="!canSubmit"
            :class="[
              'w-full py-2 px-4 rounded-md font-semibold transition-all duration-200 text-sm flex items-center justify-center border',
              canSubmit
                ? 'bg-indigo-600 hover:bg-indigo-500 text-white border-indigo-700 group'
                : 'bg-gray-600 text-gray-300 border-gray-600 opacity-60 cursor-not-allowed'
            ]"
          >
            <span v-if="!isSubmitting">ยืนยันการเติมเงิน</span>
            <i v-else class="fas fa-spinner animate-spin-pulse text-sm"></i>
          </button>
        </div>
        <!-- History Tab -->
        <div v-else class="space-y-4">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold text-indigo-300">ประวัติการเติมเงิน</h3>
            <!-- Period Selector (1 / 2 / 3 months / All) -->
            <div class="inline-flex bg-gray-800 border border-gray-700 rounded-md overflow-hidden">
              <button
                v-for="m in periodOptions"
                :key="m"
                @click="setMonths(m)"
                :class="[
                  'px-3 py-1.5 text-sm transition-colors duration-200',
                  selectedMonths === m
                    ? 'bg-indigo-600 text-white'
                    : 'text-gray-300 hover:bg-gray-700'
                ]"
              >
              
                {{ getPeriodLabel(m) }}
              </button>
            </div>
          </div>
          <!-- Summary for the selected period -->
          <div class="bg-gray-900 bg-opacity-80 border border-gray-800 rounded-md p-3">
            <p class="text-sm text-indigo-200">
              ช่วงที่เลือก:
              <span class="text-indigo-300 font-semibold">{{ periodLabel }}</span>
            </p>
            <p v-if="topupHistory.length" class="text-sm text-indigo-200 mt-1">
              ยอดเติมรวม (สำเร็จ): <span class="text-indigo-300 font-semibold">{{ totalSuccessfulTopup }} ฿</span>
            </p>
          </div>
          <div v-if="historyLoading" class="flex justify-center py-10">
            <i class="fas fa-spinner text-4xl text-indigo-400 animate-spin-pulse"></i>
          </div>
          <div
            v-else-if="historyError"
            class="text-red-300 text-center text-sm bg-gray-900 bg-opacity-80 p-3 rounded-md border border-red-600"
          >
            {{ historyError }}
            <button
              @click="fetchHistory()"
              class="mt-4 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm group"
            >
              <i class="fas fa-redo mr-1 text-sm group-hover:scale-110"></i>
              ลองใหม่
            </button>
          </div>
          <div v-else>
            <div
              v-if="!topupHistory.length"
              class="text-indigo-200 text-center py-10 text-sm bg-gray-900 bg-opacity-80 border border-gray-800 rounded-md"
            >
              ไม่มีประวัติการเติมเงินในช่วง {{ periodLabel }}
            </div>
            <ul v-else class="space-y-4">
              <li
                v-for="req in topupHistory"
                :key="req.id"
                class="flex justify-between items-center bg-gray-900 bg-opacity-80 border border-gray-800 rounded-md p-3"
              >
                <div class="text-sm">
                  <p class="font-semibold text-indigo-300">จำนวน: {{ req.amount }} ฿</p>
                  <p class="text-indigo-200">Cash: {{ req.cash }}</p>
                  <p class="text-indigo-200">โอน: {{ formatDate(req.transferTime) }}</p>
                  <p class="text-indigo-200">ยื่น: {{ formatDate(req.createdAt) }}</p>
                </div>
                <span :class="statusClass(req.status)" class="px-3 py-1 rounded-full font-semibold text-xs">
                  {{ statusText(req.status) }}
                </span>
              </li>
            </ul>
          </div>
        </div>
        <!-- End History -->
      </div>
    </div>
  </div>
</template>
<script>
import { ref, onMounted, watch, computed } from 'vue';
import { projectFirestore, projectFunctions, projectStorage } from '@/firebase/config';
import getUser from '@/composables/getUser';
export default {
  name: 'TopupComponent',
  setup() {
    const { user } = getUser();
    const isSubmitting = ref(false);
    const selectedOption = ref(null);
    const selectedFile = ref(null);
    const fileError = ref(null);
    const transferTime = ref('');
    const transferTimeError = ref(null);
    const acceptTerms = ref(false);
    const notification = ref(null);
    const activeTab = ref('topup');
    // History
    const historyLoading = ref(false);
    const historyError = ref(null);
    const topupHistory = ref([]);
    // Server status
    const online = ref(false);
    const serverStatusError = ref(null);
    // Bonus
    const bonusPercentage = ref(0);
    const bonusEndTime = ref(null);
    const bonusMessage = ref('');
    const bonusCountdown = ref('');
    let countdownInterval = null;
    // Period (months) selector: 1 / 2 / 3 / all
    const selectedMonths = ref('1');
    const allowedAllUids = ['vPehA109SpTZjMhqOuwpGHB6RFP2', 'f4NOfiFQ3uQ9nLR2phur4qT7uw73'];
    const periodOptions = computed(() => {
      const options = [1, 2, 3];
      if (user.value && allowedAllUids.includes(user.value.uid)) {
        options.push('all');
      }
      return options;
    });
    const topupOptions = ref([
      { amount: 20, cash: 20 },
      { amount: 50, cash: 50 },
      { amount: 100, cash: 100 },
      { amount: 300, cash: 300 },
      { amount: 500, cash: 500 },
      { amount: 1000, cash: 1050 },
    ]);
    const tabs = ref([
      { id: 'topup', name: 'เติมเงิน' },
      { id: 'history', name: 'ประวัติ' },
    ]);
    // Fetch server online status
    const fetchServerStatus = async () => {
      try {
        const doc = await projectFirestore.collection('server').doc('admin').get();
        if (doc.exists) {
          online.value = doc.data().online || false;
        } else {
          serverStatusError.value = 'ไม่พบข้อมูลสถานะทีมงาน';
          online.value = false;
        }
      } catch (err) {
        serverStatusError.value = `เกิดข้อผิดพลาดในการตรวจสอบสถานะ: ${err.message}`;
        online.value = false;
      }
    };
    // Fetch bonus info (realtime)
    const fetchBonusInfo = () => {
      const unsubscribe = projectFirestore.collection('server').doc('bonus').onSnapshot(
        (doc) => {
          if (doc.exists) {
            const data = doc.data();
            bonusPercentage.value = data.bonusPercentage || 0;
            bonusEndTime.value = data.bonusEndTime?.toDate() || null;
            bonusMessage.value = data.bonusMessage || '';
            updateCountdown();
          } else {
            resetBonus();
          }
        },
        (err) => {
          console.error('Error fetching bonus info:', err);
          resetBonus();
        }
      );
      return unsubscribe;
    };
    const resetBonus = () => {
      bonusPercentage.value = 0;
      bonusEndTime.value = null;
      bonusMessage.value = '';
      bonusCountdown.value = '';
    };
    const updateCountdown = () => {
      if (countdownInterval) clearInterval(countdownInterval);
      countdownInterval = setInterval(() => {
        if (bonusEndTime.value) {
          const now = new Date();
          const diff = bonusEndTime.value - now;
          if (diff > 0) {
            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);
            bonusCountdown.value = `${days}วัน ${hours}ชั่วโมง ${minutes}นาที ${seconds}วินาที`;
          } else {
            bonusCountdown.value = 'หมดเวลา';
            resetBonus();
            clearInterval(countdownInterval);
          }
        }
      }, 1000);
    };
    const bonusActive = computed(
      () => bonusPercentage.value > 0 && bonusEndTime.value && new Date() < bonusEndTime.value
    );
    const calculateCashWithBonus = (baseCash) => {
      if (bonusActive.value) {
        return Math.floor(baseCash * (1 + bonusPercentage.value / 100));
      }
      return baseCash;
    };
    const selectTopupOption = (opt) => {
      selectedOption.value = opt;
    };
    const handleFileUpload = (e) => {
      fileError.value = null;
      const f = e.target.files[0];
      if (!f) {
        selectedFile.value = null;
        return;
      }
      if (!['image/jpeg', 'image/png'].includes(f.type)) {
        fileError.value = 'ไฟล์ต้องเป็น .jpg หรือ .png เท่านั้น';
        selectedFile.value = null;
        return;
      }
      if (f.size > 5 * 1024 * 1024) {
        fileError.value = 'ไฟล์ขนาดต้องไม่เกิน 5MB';
        selectedFile.value = null;
        return;
      }
      selectedFile.value = f;
    };
    const canSubmit = computed(() => {
      return !!selectedOption.value && !!selectedFile.value && !!transferTime.value && acceptTerms.value && !isSubmitting.value;
    });
    const submitTopupRequest = async () => {
      if (!user.value) {
        showError('กรุณาล็อกอินก่อน');
        return;
      }
      if (!canSubmit.value) {
        showError('กรุณากรอกข้อมูลให้ครบถ้วน');
        return;
      }
      isSubmitting.value = true;
      try {
        const path = `topup_receipts/${user.value.uid}/${Date.now()}_${selectedFile.value.name}`;
        const refStorage = projectStorage.ref(path);
        await refStorage.put(selectedFile.value);
        const receiptUrl = await refStorage.getDownloadURL();
        const cashWithBonus = calculateCashWithBonus(selectedOption.value.cash);
        const fn = projectFunctions.httpsCallable('gameActions');
        const res = await fn({
          action: 'characterActions',
          subAction: 'submitTopupRequest',
          amount: selectedOption.value.amount,
          cash: cashWithBonus,
          receiptUrl,
          transferTime: new Date(transferTime.value).toISOString(),
          acceptTerms: acceptTerms.value,
          bonusApplied: bonusActive.value ? bonusPercentage.value : 0,
        });
        if (res.data?.error) {
          throw new Error(res.data.error);
        }
        showSuccess('ส่งคำขอสำเร็จ รอแอดมินตรวจสอบ');
        resetForm();
        if (activeTab.value === 'history') {
          await fetchHistory(); // refresh with current period
        }
      } catch (err) {
        showError(`เกิดข้อผิดพลาด: ${err.message}`);
      } finally {
        isSubmitting.value = false;
      }
    };
    // ===== History (with period filter) =====
    const setMonths = (m) => {
      if (selectedMonths.value !== m) {
        selectedMonths.value = m;
        fetchHistory();
      }
    };
    const getPeriodLabel = (m) => {
      return m === 'all' ? 'ดูทั้งหมด' : `${m} เดือน`;
    };
    const periodLabel = computed(() => {
      const m = selectedMonths.value;
      return m === 'all' ? 'ดูทั้งหมด' : `${m} เดือนล่าสุด`;
    });
    const getCutoffDate = (monthsBack) => {
      const d = new Date();
      // เลื่อนไปย้อนหลังตามจำนวนเดือน (ประมาณการ: setMonth รองรับข้ามปีอัตโนมัติ)
      d.setMonth(d.getMonth() - monthsBack);
      return d;
    };
    const fetchHistory = async () => {
      if (!user.value) {
        historyError.value = 'กรุณาล็อกอินเพื่อดูประวัติ';
        return;
      }
      historyLoading.value = true;
      historyError.value = null;
      try {
        let query = projectFirestore
          .collection('topupRequests')
          .where('uid', '==', user.value.uid);

        if (selectedMonths.value !== 'all') {
          const cutoff = getCutoffDate(selectedMonths.value);
          query = query.where('createdAt', '>=', cutoff);
        }

        const snap = await query
          .orderBy('createdAt', 'desc')
          .get();
        topupHistory.value = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (err) {
        historyError.value = `โหลดประวัติไม่สำเร็จ: ${err.message}`;
      } finally {
        historyLoading.value = false;
      }
    };
    const totalSuccessfulTopup = computed(() => {
      return topupHistory.value
        .filter((req) => req.status === 'success')
        .reduce((sum, req) => sum + (Number(req.amount) || 0), 0);
    });
    // ===== Helpers =====
    const showSuccess = (msg) => {
      notification.value = { type: 'success', message: msg };
      setTimeout(() => (notification.value = null), 3000);
    };
    const showError = (msg) => {
      notification.value = { type: 'error', message: msg };
      setTimeout(() => (notification.value = null), 3000);
    };
    const resetForm = () => {
      selectedOption.value = null;
      selectedFile.value = null;
      transferTime.value = '';
      acceptTerms.value = false;
      fileError.value = null;
      transferTimeError.value = null;
    };
    const formatDate = (ts) => {
      // รองรับ Timestamp ({seconds}) หรือ Date/string
      try {
        if (ts?.seconds) {
          return new Date(ts.seconds * 1000).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' });
        }
        const d = new Date(ts);
        if (!isNaN(d.getTime())) {
          return d.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' });
        }
      } catch (e) {}
      return 'ไม่ระบุ';
    };
    const statusText = (status) => {
      const statusMap = { pending: 'กำลังดำเนินการ', success: 'สำเร็จ', failed: 'ไม่สำเร็จ' };
      return statusMap[status] || status;
    };
    const statusClass = (status) => {
      const classMap = {
        pending: 'bg-yellow-600 text-white',
        success: 'bg-green-600 text-white',
        failed: 'bg-red-600 text-white',
      };
      return classMap[status] || 'bg-gray-600 text-white';
    };
    // ===== Lifecycle =====
    onMounted(() => {
      fetchServerStatus();
      fetchBonusInfo();
      // ถ้าเปิดแท็บประวัติไว้ ให้โหลดตามช่วง
      if (activeTab.value === 'history') {
        fetchHistory();
      }
    });
    watch(activeTab, (newTab) => {
      if (newTab === 'history') {
        fetchHistory();
      } else if (newTab === 'topup') {
        fetchServerStatus();
      }
    });
    return {
      user,
      // Topup
      topupOptions,
      selectedOption,
      selectTopupOption,
      selectedFile,
      handleFileUpload,
      fileError,
      transferTime,
      transferTimeError,
      acceptTerms,
      notification,
      isSubmitting,
      canSubmit,
      submitTopupRequest,
      // Tabs
      activeTab,
      tabs,
      // Server status
      online,
      serverStatusError,
      // Bonus
      bonusActive,
      bonusPercentage,
      bonusMessage,
      bonusCountdown,
      calculateCashWithBonus,
      // History
      historyLoading,
      historyError,
      topupHistory,
      fetchHistory,
      totalSuccessfulTopup,
      formatDate,
      statusText,
      statusClass,
      // Period filter
      selectedMonths,
      setMonths,
      periodOptions,
      getPeriodLabel,
      periodLabel,
    };
  },
};
</script>
<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fcondo.png?alt=media&token=198ebb1c-66e5-4bf0-aa79-32bff287aa09') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}
/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #2d3748; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4a5568; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #6b7280; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #4a5568 #2d3748; }
/* Animations */
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }
@keyframes tabSlide { from { width: 0; } to { width: 100%; } }
.animate-tabSlide { animation: tabSlide 0.2s ease-out; }
@keyframes spin-pulse { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
.animate-spin-pulse { animation: spin-pulse 1s infinite linear; }
/* Fade Transition */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
/* Responsive */
@media (max-width: 639px) {
  .text-sm { font-size: 0.75rem; }
  .text-base { font-size: 0.875rem; }
  .p-4 { padding: 0.75rem; }
}
</style>